import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveIcon from "@mui/icons-material/Save";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import GlassCard from "../../components/dashboard/GlassCard";
import Header from "../../components/Header";
import saasApi from "../../utils/saasApi";

const DETECTIONS = {
  shoplifting: {
    label: "Robo / hurto",
    params: [
      ["min_confidence", "Umbral confianza", 0, 1, 0.01],
      ["sensitivity", "Sensibilidad", 0, 1, 0.01],
      ["cooldown_s", "Cooldown (s)", 0, 300, 1],
      ["fps", "FPS analisis", 1, 30, 1],
      ["min_track_s", "Track minimo (s)", 0, 20, 0.5],
    ],
  },
  people_count: {
    label: "Conteo personas",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["fps", "FPS", 1, 30, 1], ["count_interval_s", "Intervalo conteo (s)", 1, 300, 1]],
  },
  queue: {
    label: "Fila / cola",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["sensitivity", "Sensibilidad", 0, 1, 0.01], ["max_wait_s", "Espera maxima (s)", 10, 1800, 10]],
  },
  heatmap: {
    label: "Heatmap",
    params: [["fps", "FPS", 1, 15, 1], ["aggregation_s", "Agregacion (s)", 10, 3600, 10], ["sensitivity", "Sensibilidad", 0, 1, 0.01]],
  },
  weapon: {
    label: "Arma",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["cooldown_s", "Cooldown (s)", 0, 300, 1], ["fps", "FPS", 1, 30, 1]],
  },
  fall: {
    label: "Caida",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["cooldown_s", "Cooldown (s)", 0, 300, 1], ["stationary_s", "Tiempo inmovil (s)", 1, 120, 1]],
  },
  fire: {
    label: "Fuego",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["sensitivity", "Sensibilidad", 0, 1, 0.01], ["cooldown_s", "Cooldown (s)", 0, 300, 1]],
  },
  smoke: {
    label: "Humo",
    params: [["min_confidence", "Umbral", 0, 1, 0.01], ["sensitivity", "Sensibilidad", 0, 1, 0.01], ["cooldown_s", "Cooldown (s)", 0, 300, 1]],
  },
};

const defaultConfig = {
  min_confidence: 0.65,
  sensitivity: 0.7,
  cooldown_s: 30,
  fps: 8,
};

const emptyZones = { zones: [], lines: [], regions: [], queue_regions: [], heatmap: { enabled: false } };

const toYaml = (value, indent = 0) => {
  const pad = " ".repeat(indent);
  if (Array.isArray(value)) {
    return value.map((item) => `${pad}- ${typeof item === "object" ? `\n${toYaml(item, indent + 2)}` : item}`).join("\n");
  }
  if (value && typeof value === "object") {
    return Object.entries(value).map(([key, item]) => {
      if (item && typeof item === "object") return `${pad}${key}:\n${toYaml(item, indent + 2)}`;
      return `${pad}${key}: ${item}`;
    }).join("\n");
  }
  return `${pad}${value}`;
};

const parseExpert = (text) => {
  try {
    return JSON.parse(text || "{}");
  } catch (_) {
    const result = {};
    String(text || "").split(/\r?\n/).forEach((line) => {
      const clean = line.trim();
      if (!clean || clean.startsWith("#") || !clean.includes(":")) return;
      const [key, ...rest] = clean.split(":");
      const raw = rest.join(":").trim();
      if (raw === "true" || raw === "false") result[key.trim()] = raw === "true";
      else if (raw !== "" && !Number.isNaN(Number(raw))) result[key.trim()] = Number(raw);
      else result[key.trim()] = raw;
    });
    return result;
  }
};

const CameraAIConfig = ({ commercialSetup = false }) => {
  const svgRef = useRef(null);
  const [tab, setTab] = useState("visual");
  const [cameras, setCameras] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [history, setHistory] = useState([]);
  const [cameraId, setCameraId] = useState("");
  const [detection, setDetection] = useState("shoplifting");
  const [enabled, setEnabled] = useState(true);
  const [params, setParams] = useState(defaultConfig);
  const [zones, setZones] = useState(emptyZones);
  const [drawMode, setDrawMode] = useState("zone");
  const [draftPoints, setDraftPoints] = useState([]);
  const [expertText, setExpertText] = useState("{}");
  const [summary, setSummary] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback((message, severity = "success") => {
    setSnack({ open: true, message, severity });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cameraPayload, configPayload] = await Promise.all([
        commercialSetup ? saasApi.getBillingSetupCameras() : saasApi.getCameras(),
        saasApi.getDetectionConfig(cameraId ? { camera_id: cameraId, include_history: 1 } : { include_history: 1 }),
      ]);
      const rows = cameraPayload.cameras || [];
      setCameras(rows);
      setConfigs(configPayload.configs || []);
      if (!cameraId && rows[0]) setCameraId(String(rows[0].id));
    } catch (err) {
      notify(err.message, "error");
    } finally {
      setLoading(false);
    }
  }, [cameraId, commercialSetup, notify]);

  const loadHistory = useCallback(async () => {
    if (!cameraId || !detection) return;
    try {
      const rows = await saasApi.getDetectionConfigHistory({ camera_id: cameraId, detection_type: detection });
      setHistory(rows);
    } catch (err) {
      notify(err.message, "error");
    }
  }, [cameraId, detection, notify]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const activeConfig = useMemo(() => {
    return configs
      .filter((item) => String(item.camera_id) === String(cameraId) && item.detection_type === detection)
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0))[0];
  }, [cameraId, configs, detection]);

  const selectedCamera = useMemo(() => cameras.find((camera) => String(camera.id) === String(cameraId)), [cameraId, cameras]);

  useEffect(() => {
    const base = activeConfig?.config_json || {};
    const parsed = typeof base === "string" ? JSON.parse(base || "{}") : base;
    const nextParams = { ...defaultConfig, ...Object.fromEntries(Object.entries(parsed).filter(([key]) => key !== "zones_config")) };
    const nextZones = parsed.zones_config || emptyZones;
    setEnabled(activeConfig ? Boolean(activeConfig.enabled) : true);
    setParams(nextParams);
    setZones({ ...emptyZones, ...nextZones });
    setExpertText(toYaml({ ...nextParams, zones_config: nextZones }));
    setSummary("");
  }, [activeConfig]);

  const updateParam = (key, value) => {
    setParams((current) => ({ ...current, [key]: value }));
  };

  const svgPoint = (event) => {
    const rect = svgRef.current.getBoundingClientRect();
    return {
      x: Number((((event.clientX - rect.left) / rect.width) * 100).toFixed(2)),
      y: Number((((event.clientY - rect.top) / rect.height) * 100).toFixed(2)),
    };
  };

  const handleCanvasClick = (event) => {
    const point = svgPoint(event);
    if (drawMode === "line") {
      const next = [...draftPoints, point];
      if (next.length === 2) {
        setZones((current) => ({
          ...current,
          lines: [...(current.lines || []), { id: `line_${Date.now()}`, label: `Cruce ${(current.lines || []).length + 1}`, points: next, direction: "both" }],
        }));
        setDraftPoints([]);
      } else {
        setDraftPoints(next);
      }
      return;
    }
    setDraftPoints((current) => [...current, point]);
  };

  const finishShape = () => {
    if (draftPoints.length < 3) {
      notify("Un poligono requiere al menos 3 puntos", "warning");
      return;
    }
    const key = drawMode === "queue" ? "queue_regions" : drawMode === "region" ? "regions" : "zones";
    setZones((current) => ({
      ...current,
      [key]: [...(current[key] || []), { id: `${key}_${Date.now()}`, label: `${drawMode} ${(current[key] || []).length + 1}`, points: draftPoints }],
    }));
    setDraftPoints([]);
  };

  const rollbackDraft = () => {
    setDraftPoints([]);
    setZones(emptyZones);
  };

  const validate = () => {
    if (!cameraId) return "Selecciona una camara";
    if (!detection) return "Selecciona una deteccion";
    if ((zones.zones || []).some((zone) => !zone.points || zone.points.length < 3)) return "Hay zonas invalidas";
    if ((zones.lines || []).some((line) => !line.points || line.points.length !== 2)) return "Hay lineas invalidas";
    return "";
  };

  const save = async () => {
    const error = validate();
    if (error) {
      notify(error, "error");
      return;
    }
    try {
      const expert = tab === "expert" ? parseExpert(expertText) : params;
      const config_json = {
        ...expert,
        zones_config: zones,
      };
      const result = await saasApi.saveDetectionConfig({
        camera_id: Number(cameraId),
        detection_type: detection,
        enabled,
        config_json,
        summary: summary || undefined,
      });
      notify(`Version ${result.version} guardada. Motor IA pendiente de recarga segura.`);
      await Promise.all([load(), loadHistory()]);
    } catch (err) {
      notify(err.message || "Configuracion invalida", "error");
    }
  };

  const rollback = async (versionId) => {
    try {
      const result = await saasApi.rollbackDetectionConfig(versionId);
      notify(`Rollback aplicado como version ${result.version}`);
      await Promise.all([load(), loadHistory()]);
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const markApplied = async (versionId) => {
    try {
      await saasApi.markDetectionConfigApplied(versionId);
      notify("Recarga marcada como aplicada");
      await Promise.all([load(), loadHistory()]);
    } catch (err) {
      notify(err.message, "error");
    }
  };

  const detectionSpec = DETECTIONS[detection] || DETECTIONS.shoplifting;
  const previewSrc = selectedCamera && !commercialSetup ? `/snapshot/${encodeURIComponent(selectedCamera.name)}` : "";

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box className="scene-toolbar" display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={2}>
        <Header title="Config IA por camara" subtitle="Modo visual, experto YAML, zonas, versionado y rollback" />
        <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={!cameraId}>Guardar version</Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={3}>
          <GlassCard hover={false}>
            <Stack spacing={2}>
              <Stack direction="row" gap={1} alignItems="center">
                <SettingsSuggestOutlinedIcon color="primary" />
                <Typography variant="h5">Scope</Typography>
              </Stack>
              <TextField select label="Camara" value={cameraId} onChange={(event) => setCameraId(event.target.value)} fullWidth>
                {cameras.map((camera) => <MenuItem key={camera.id} value={camera.id}>{camera.display_name || camera.name}</MenuItem>)}
              </TextField>
              <TextField select label="Deteccion" value={detection} onChange={(event) => setDetection(event.target.value)} fullWidth>
                {Object.entries(DETECTIONS).map(([key, item]) => <MenuItem key={key} value={key}>{item.label}</MenuItem>)}
              </TextField>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography>Activa</Typography>
                <Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
              </Stack>
              {activeConfig ? (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Chip label={`Version ${activeConfig.version}`} color="primary" />
                  <Chip label={activeConfig.reload_required ? "Recarga pendiente" : "Aplicada"} color={activeConfig.reload_required ? "warning" : "success"} />
                </Stack>
              ) : (
                <Alert severity="info">Sin configuracion propia para esta camara/deteccion.</Alert>
              )}
            </Stack>
          </GlassCard>
        </Grid>

        <Grid item xs={12} lg={9}>
          <GlassCard hover={false}>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
              <Tab value="visual" label="Modo visual cliente" />
              <Tab value="zones" label="Editor visual de zonas" />
              <Tab value="expert" label="Modo experto YAML" />
              {!commercialSetup && <Tab value="versions" label="Versiones" />}
            </Tabs>

            {tab === "visual" && (
              <Grid container spacing={3}>
                {detectionSpec.params.map(([key, label, min, max, step]) => (
                  <Grid item xs={12} md={6} key={key}>
                    <Typography fontWeight={600}>{label}</Typography>
                    <Stack direction="row" gap={2} alignItems="center">
                      <Slider value={Number(params[key] ?? min)} min={min} max={max} step={step} onChange={(_, value) => updateParam(key, value)} />
                      <TextField size="small" type="number" value={params[key] ?? min} onChange={(event) => updateParam(key, Number(event.target.value))} sx={{ width: 120 }} />
                    </Stack>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <TextField label="Resumen del cambio" value={summary} onChange={(event) => setSummary(event.target.value)} fullWidth />
                </Grid>
              </Grid>
            )}

            {tab === "zones" && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Stack direction="row" gap={1} mb={2} flexWrap="wrap">
                    <ToggleButtonGroup exclusive value={drawMode} onChange={(_, value) => value && setDrawMode(value)} size="small">
                      <ToggleButton value="zone">Zona</ToggleButton>
                      <ToggleButton value="line">Linea cruce</ToggleButton>
                      <ToggleButton value="region">Region</ToggleButton>
                      <ToggleButton value="queue">Fila</ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="outlined" onClick={finishShape}>Cerrar poligono</Button>
                    <Button variant="outlined" startIcon={<UndoIcon />} onClick={() => setDraftPoints((points) => points.slice(0, -1))}>Deshacer punto</Button>
                    <Button variant="outlined" color="warning" startIcon={<RestartAltIcon />} onClick={rollbackDraft}>Limpiar visual</Button>
                  </Stack>
                  <Box sx={{ position: "relative", bgcolor: "#020617", borderRadius: "8px", overflow: "hidden", aspectRatio: "16 / 9" }}>
                    {previewSrc && (
                      <Box component="img" src={previewSrc} alt="preview" sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.85 }} />
                    )}
                    <Box component="svg" ref={svgRef} onClick={handleCanvasClick} viewBox="0 0 100 100" preserveAspectRatio="none" sx={{ position: "absolute", inset: 0, cursor: "crosshair" }}>
                      {(zones.zones || []).map((zone) => <polygon key={zone.id} points={zone.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(34,197,94,0.22)" stroke="#22c55e" strokeWidth="0.6" />)}
                      {(zones.regions || []).map((region) => <polygon key={region.id} points={region.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(59,130,246,0.22)" stroke="#3b82f6" strokeWidth="0.6" />)}
                      {(zones.queue_regions || []).map((region) => <polygon key={region.id} points={region.points.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(245,158,11,0.22)" stroke="#f59e0b" strokeWidth="0.6" />)}
                      {(zones.lines || []).map((line) => <line key={line.id} x1={line.points[0].x} y1={line.points[0].y} x2={line.points[1].x} y2={line.points[1].y} stroke="#ef4444" strokeWidth="0.8" />)}
                      {draftPoints.length > 0 && <polyline points={draftPoints.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#e0f2fe" strokeWidth="0.5" strokeDasharray="2 1" />}
                      {draftPoints.map((point, index) => <circle key={`${point.x}-${point.y}-${index}`} cx={point.x} cy={point.y} r="1" fill="#fff" />)}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack spacing={2}>
                    <Alert severity="info">Click sobre la preview para dibujar. Las coordenadas se guardan normalizadas 0-100 por camara/deteccion.</Alert>
                    <FormControlLabel control={<Switch checked={Boolean(zones.heatmap?.enabled)} onChange={(event) => setZones((current) => ({ ...current, heatmap: { ...(current.heatmap || {}), enabled: event.target.checked } }))} />} label="Activar heatmap" />
                    <Typography>Zonas: {(zones.zones || []).length}</Typography>
                    <Typography>Lineas: {(zones.lines || []).length}</Typography>
                    <Typography>Regiones: {(zones.regions || []).length}</Typography>
                    <Typography>Filas: {(zones.queue_regions || []).length}</Typography>
                    <Divider />
                    <Typography variant="h6">Preview config</Typography>
                    <Box component="pre" sx={{ maxHeight: 280, overflow: "auto", fontSize: 12, color: "text.secondary", fontFamily: (theme) => theme.custom.monoFont, bgcolor: (theme) => theme.custom.surface.base, p: 1, borderRadius: 1 }}>
                      {JSON.stringify(zones, null, 2)}
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            )}

            {tab === "expert" && (
              <Stack spacing={2}>
                <Alert severity="warning">Modo soporte/admin avanzado. Acepta JSON completo o YAML simple key: value. El bloque visual se mantiene en zones_config.</Alert>
                <TextField value={expertText} onChange={(event) => setExpertText(event.target.value)} multiline minRows={22} fullWidth spellCheck={false} sx={{ fontFamily: (theme) => theme.custom.monoFont }} />
              </Stack>
            )}

            {tab === "versions" && (
              <Stack spacing={1.5}>
                {history.length === 0 && <Alert severity="info">No hay versiones para esta camara/deteccion.</Alert>}
                {history.map((item) => (
                  <Box key={item.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px", p: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                      <Box>
                        <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
                          <Chip label={`Version ${item.version}`} color={item.is_active ? "primary" : "default"} />
                          <Chip label={item.enabled ? "Activa" : "Desactivada"} color={item.enabled ? "success" : "default"} />
                          <Chip label={item.reload_required ? "Recarga pendiente" : "Aplicada"} color={item.reload_required ? "warning" : "success"} />
                        </Stack>
                        <Typography fontWeight={600}>{item.summary || "Sin resumen"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Autor: {item.author_email || item.author_username || item.created_by || "Sistema"} · Fecha: {item.created_at ? new Date(item.created_at).toLocaleString("es-AR") : "-"}
                        </Typography>
                        <Tooltip title={JSON.stringify(item.diff_json || {}, null, 2)}>
                          <Typography variant="body2" color="text.secondary" noWrap>Diff: {JSON.stringify(item.diff_json || {})}</Typography>
                        </Tooltip>
                      </Box>
                      <Stack direction="row" gap={1} alignItems="center">
                        {item.reload_required ? <Button size="small" onClick={() => markApplied(item.id)}>Marcar aplicada</Button> : null}
                        <Button size="small" variant="outlined" startIcon={<HistoryIcon />} onClick={() => rollback(item.id)}>Volver</Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </GlassCard>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={4200} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CameraAIConfig;
